import React, { useRef, useState, useEffect } from "react";
import groupByConsecutiveSpeakers from "../../../../services/GroupSegments";
import SegmentGroup from "./SegmentGroup";
import styles from "./styles.module.scss";
import SegmentPopup from "./SegmentPopup";
import { useDispatch, useSelector } from "react-redux";
import { updateDataset } from "../../../../redux/dataset/datasetSlice";
import swal from "sweetalert";
import TranscriptModerator from "../TranscriptModerator";
import SpeakerList from "../SpeakerList";
import ExtraButtons from "../ExtraButtons";
import ScrollToTop from "../ScrollToTop";
import hasEmptySpeaker from "../../../../services/CheckEmptySpeakers";
import ExportTranscript from "../ExportTranscript";
import DebugPanel from "./DebugPanel";

function Transcript() {
  const data = useSelector((state) => state.datasetSlice.dataset);
  const headerHeight = useSelector((state) => state.headerSlice.height);
  const stickyAsideRef = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [selectedRange, setSelectedRange] = useState({
    start: null,
    end: null,
  });
  const dispatch = useDispatch();
  const [currentPlayingSegment, setCurrentPlayingSegment] = useState(null);
  const [currentPlayingWord, setCurrentPlayingWord] = useState(null);
  const lastWordIndexRef = useRef(0); // Persistent cache for word search optimization
  const precomputedStartsRef = useRef(null); // Float32Array of word starts
  const precomputedEndsRef = useRef(null);   // Float32Array of word ends
  const lastAlgoTickRef = useRef(0);         // Throttle gate for timeupdate
  const stopEditing = data.transcript_status === "COMPLETED" ? true : false;
  const mediaRef = useRef(null);
  const isVideo = /^(mp4|webm|ogg)$/i.test(data.file_type);

  const [showSearchReplace, setShowSearchReplace] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const searchReplaceRef = useRef(null);
  const [isInAside, setIsInAside] = useState(false); // Moved here - before useEffect that uses it!
  const [editingWord, setEditingWord] = useState(null);
  const [editingWordText, setEditingWordText] = useState("");
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, word: null, wordIndex: null });
  const [rightClickCount, setRightClickCount] = useState(0);
  const [rightClickTimer, setRightClickTimer] = useState(null);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [editingSpeakerText, setEditingSpeakerText] = useState("");
  const [wordsBySpeaker, setWordsBySpeaker] = useState([]);

  // Create wordsBySpeaker when data changes
  useEffect(() => {
    if (data.transcript.words && data.transcript.words.length > 0) {
      const wordsBySpeaker = [];
      let currentSpeaker = null;
      let currentWordGroup = [];
      
      data.transcript.words.forEach((word, index) => {
        // Find which segment this word belongs to and get the speaker
        const wordSegment = data.transcript.segments.find(
          segment => word.start >= segment.start && word.end <= segment.end
        );
        const speaker = wordSegment ? wordSegment.speaker : word.speaker || 'UNKNOWN';
        
        if (speaker !== currentSpeaker) {
          // New speaker - save previous group and start new one
          if (currentWordGroup.length > 0) {
            wordsBySpeaker.push({
              speaker: currentSpeaker,
              words: currentWordGroup
            });
          }
          currentSpeaker = speaker;
          currentWordGroup = [word];
        } else {
          // Same speaker - add to current group
          currentWordGroup.push(word);
        }
      });
      
      // Don't forget the last group
      if (currentWordGroup.length > 0) {
        wordsBySpeaker.push({
          speaker: currentSpeaker,
          words: currentWordGroup
        });
      }
      
      setWordsBySpeaker(wordsBySpeaker);
    }
  }, [data.transcript.words, data.transcript.segments]);

  // Use wordsBySpeaker state instead of IIFE
  const wordsBySpeakerToRender = wordsBySpeaker;


  // Global tuneables
  const ENTER_THRESHOLD_S = 0.08; // 80ms enter window
  const EXIT_THRESHOLD_S = 0.12;  // 120ms exit window
  const TIMEUPDATE_MIN_INTERVAL_MS = 140; // ~7 Hz
  const ALIGN_OFFSET_S = 0.06; // Align slightly inside the word when jumping

  const handleSearchReplace = () => {
    if (!searchText) return;

    swal({
      title: "Confirm Replace All",
      text: `Replace all (${data.transcript.segments.filter(segment => segment.text.includes(searchText)).length}) occurrences of "${searchText}" with "${replaceText}"?`,
      icon: "warning",
      buttons: ["Cancel", "Replace All"],
      dangerMode: true,
    }).then((willReplace) => {
      if (willReplace) {
        const newSegments = data.transcript.segments.map(segment => ({
          ...segment,
          text: segment.text.replaceAll(searchText, replaceText)
        }));

        const newRawText = newSegments.map(segment => segment.text).join("");

        dispatch(
          updateDataset({
            transcript: { text: newRawText, segments: newSegments },
          })
        );

        setShowSearchReplace(false);
        setSearchText('');
        setReplaceText('');
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchReplaceRef.current && !searchReplaceRef.current.contains(event.target)) {
        setShowSearchReplace(false);
        setSearchText('');
        setReplaceText('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Precompute typed arrays for faster binary searches
    if (data?.transcript?.words && data.transcript.words.length > 0) {
      const starts = new Float32Array(data.transcript.words.length);
      const ends = new Float32Array(data.transcript.words.length);
      for (let i = 0; i < data.transcript.words.length; i++) {
        const w = data.transcript.words[i];
        starts[i] = w.start || 0;
        ends[i] = w.end || w.start || 0;
      }
      precomputedStartsRef.current = starts;
      precomputedEndsRef.current = ends;
      // Reset warm cache near beginning of array to keep scans short after dataset swaps
      lastWordIndexRef.current = Math.min(lastWordIndexRef.current, data.transcript.words.length - 1);
      if (lastWordIndexRef.current < 0) lastWordIndexRef.current = 0;
    } else {
      precomputedStartsRef.current = null;
      precomputedEndsRef.current = null;
      lastWordIndexRef.current = 0;
    }
  }, [data?.transcript?.words]);

  useEffect(() => {
    const handleTimeUpdate = () => {
      // CRITICAL: Read mediaRef.current INSIDE handler to always get current player
      const mediaElement = mediaRef.current;
      if (!mediaElement) return;
      
      // Throttle processing to reduce jitter and CPU
      const now = performance.now();
      if (now - lastAlgoTickRef.current < TIMEUPDATE_MIN_INTERVAL_MS) {
        return;
      }
      lastAlgoTickRef.current = now;

      const playbackTime = mediaElement.currentTime;

      // Find currently playing segment (for word-level highlighting, we don't need segment highlighting)
      const playingSegment = data.transcript.segments.find(
        (segment) =>
          playbackTime >= segment.start && playbackTime <= segment.end
      );

      // Don't set currentPlayingSegment to avoid segment highlighting
      // setCurrentPlayingSegment is only used for word-level highlighting now

       // Find currently playing word (if word-level data exists)
       // ENHANCED: Direct timestamp search + warm-cache scan + hysteresis
       if (data.transcript.words && data.transcript.words.length > 0) {
         const words = data.transcript.words;
         let playingWord = null;
         const lastWordIndex = lastWordIndexRef.current;
         const startsArr = precomputedStartsRef.current;
         const endsArr = precomputedEndsRef.current;
         
         // Detect jump magnitude
         const lastPlaybackTime = mediaElement.getAttribute('data-last-time') || '0';
         const timeDiff = Math.abs(playbackTime - parseFloat(lastPlaybackTime));
         const isBigJump = timeDiff > 5; // More than 5s jump
         const isHugeJump = timeDiff > 60; // More than 1 minute jump
         
         // Store current time for next comparison
         mediaElement.setAttribute('data-last-time', playbackTime.toString());
         
         // Direct timestamp search using binary search over precomputed arrays for big/huge jumps
         if ((isHugeJump || isBigJump) && words.length > 0 && startsArr && endsArr) {
           // Binary search: first start >= playbackTime
           let left = 0;
           let right = startsArr.length - 1;
           let candidateIdx = startsArr.length - 1;
           while (left <= right) {
             const mid = (left + right) >> 1;
             if (startsArr[mid] >= playbackTime) {
               candidateIdx = mid;
               right = mid - 1;
             } else {
               left = mid + 1;
             }
           }
           // Compare candidate with previous to choose closest
           const pickIdx = (() => {
             const prev = Math.max(0, candidateIdx - 1);
             const aStart = startsArr[candidateIdx];
             const aEnd = endsArr[candidateIdx];
             const bStart = startsArr[prev];
             const bEnd = endsArr[prev];
             const insideA = playbackTime >= aStart && playbackTime <= aEnd;
             const insideB = playbackTime >= bStart && playbackTime <= bEnd;
             if (insideA) return candidateIdx;
             if (insideB) return prev;
             const distA = playbackTime < aStart ? aStart - playbackTime : playbackTime - aEnd;
             const distB = playbackTime < bStart ? bStart - playbackTime : playbackTime - bEnd;
             return distA <= distB ? candidateIdx : prev;
           })();
           playingWord = words[pickIdx];
           lastWordIndexRef.current = pickIdx;
         } else {
           // NORMAL PLAYBACK - Optimized linear search with closest word fallback
           // Search forward from last position (most common case)
           for (let i = lastWordIndex; i < words.length; i++) {
             const word = words[i];
            if (playbackTime >= (word.start - ENTER_THRESHOLD_S) && playbackTime <= (word.end + ENTER_THRESHOLD_S)) {
               playingWord = word;
               lastWordIndexRef.current = i;
               break;
             }
             // Early exit if we've passed current time
            if (word.start > playbackTime + ENTER_THRESHOLD_S) {
               break;
             }
           }
           
           // If not found forward, search backward
           if (!playingWord && lastWordIndex > 0) {
             for (let i = lastWordIndex - 1; i >= 0; i--) {
               const word = words[i];
               if (playbackTime >= (word.start - ENTER_THRESHOLD_S) && playbackTime <= (word.end + ENTER_THRESHOLD_S)) {
                 playingWord = word;
                 lastWordIndexRef.current = i;
                 break;
               }
               // Stop if we've gone too far back
               if (word.end < playbackTime - 5) {
                 break;
               }
             }
           }
           
           // If still no exact match, find closest word in nearby range
           if (!playingWord) {
             let closestWord = null;
             let minDistance = Infinity;
             
            const searchStart = Math.max(0, lastWordIndex - 10);
            const searchEnd = Math.min(words.length, lastWordIndex + 10);
             
             for (let i = searchStart; i < searchEnd; i++) {
               const word = words[i];
               
               // Calculate distance to word
               let distance;
              if (playbackTime >= word.start && playbackTime <= word.end) {
                 distance = 0; // Inside word
               } else if (playbackTime < word.start) {
                 distance = word.start - playbackTime; // Before word
               } else {
                 distance = playbackTime - word.end; // After word
               }
               
               if (distance < minDistance) {
                 minDistance = distance;
                 closestWord = word;
               }
             }
             
             if (closestWord) {
               playingWord = closestWord;
               lastWordIndexRef.current = closestWord.index;
             }
           }
        }

        // Hysteresis: if we already have a current word, avoid toggling off too early
        if (playingWord && currentPlayingWord != null) {
          const current = data.transcript.words[currentPlayingWord];
          if (current) {
            const withinExit = playbackTime >= (current.start - EXIT_THRESHOLD_S) && playbackTime <= (current.end + EXIT_THRESHOLD_S);
            // If still within extended window of current word and candidate is adjacent, keep current
            if (withinExit && Math.abs(playingWord.index - currentPlayingWord) <= 1) {
              playingWord = current;
              lastWordIndexRef.current = currentPlayingWord;
            }
          }
        }

        if (playingWord) {
          setCurrentPlayingWord(playingWord.index);
        } else {
          setCurrentPlayingWord(null);
        }
      }
    };

    // Attach to window to catch events from BOTH players
    const attachListener = () => {
      const mediaElement = mediaRef.current;
      if (mediaElement) {
        mediaElement.addEventListener("timeupdate", handleTimeUpdate);
      }
    };

    const removeListener = () => {
      const mediaElement = mediaRef.current;
      if (mediaElement) {
        mediaElement.removeEventListener("timeupdate", handleTimeUpdate);
      }
    };

    // Initial attach
    attachListener();

    // Re-attach when isInAside changes (player switches position)
    return removeListener;
  }, [data.transcript.segments, data.transcript.words, isInAside]); // Re-run when player switches!

  useEffect(() => {
    if (stickyAsideRef.current) {
      stickyAsideRef.current.style.top = `${headerHeight + 24}px`;
    }
  }, [headerHeight]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      closeContextMenu();
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [rightClickTimer]); // Include rightClickTimer in dependencies

  const handleSegmentClick = (segment, event) => {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    if (!selectedRange.start) {
      setSelectedRange({ start: segment, end: null });
    } else if (selectedRange.start && !selectedRange.end) {
      const startSegmentGroup = groupedSegments.find((group) =>
        group.includes(selectedRange.start)
      );
      const endSegmentGroup = groupedSegments.find((group) =>
        group.includes(segment)
      );

      if (startSegmentGroup === endSegmentGroup) {
        const startIndex = startSegmentGroup.indexOf(selectedRange.start);
        const endIndex = startSegmentGroup.indexOf(segment);
        if (endIndex > startIndex) {
          setSelectedRange((prevState) => ({ ...prevState, end: segment }));
        } else {
          return;
        }
      } else {
        setSelectedRange({ start: segment, end: null });
      }
    } else {
      setSelectedRange({ start: segment, end: null });
    }

    if (currentSegment === segment) {
      setCurrentSegment(null);
      setSelectedRange({ start: null, end: null });
    } else {
      setCurrentSegment(segment);
    }
  };

  const handleEditClick = (segment) => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setCurrentSegment(null);
    setSelectedRange({ start: null, end: null });
  };

  const handleSegmentDoubleClick = (startTime) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = Math.max(0, startTime + ALIGN_OFFSET_S);
      mediaRef.current.play();
    }
  };

  const handleWordClick = (startTime) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = Math.max(0, startTime + ALIGN_OFFSET_S);
      mediaRef.current.play();
    }
  };

  const handleWordEdit = (word, wordIndex) => {
    console.log('🔧 Long click on word:', word.word, 'index:', wordIndex);
    setEditingWord(wordIndex);
    setEditingWordText(word.word);
  };

  const handleWordSave = () => {
    if (editingWord !== null && editingWordText.trim()) {
      // Update the word in the transcript data
      const updatedWords = [...data.transcript.words];
      updatedWords[editingWord] = {
        ...updatedWords[editingWord],
        word: editingWordText.trim()
      };
      
      // Update the transcript text by reconstructing it from words
      const updatedText = updatedWords.map(w => w.word).join(' ');
      
      // Dispatch the update
      dispatch(updateDataset({
        transcript: {
          ...data.transcript,
          words: updatedWords,
          text: updatedText
        }
      }));
      
      setEditingWord(null);
      setEditingWordText("");
    }
  };

  const handleWordCancel = () => {
    setEditingWord(null);
    setEditingWordText("");
  };

  const handleContextMenu = (e, word, wordIndex) => {
    console.log('🖱️ Right-click detected on word:', word.word);
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY - 60, // Position above the word
      word: word,
      wordIndex: wordIndex
    });
  };

  const handleContextMenuAction = (action) => {
    const { word, wordIndex } = contextMenu;
    
    switch (action) {
      case 'play':
        handleWordClick(word.start);
        break;
      case 'edit':
        handleWordEdit(word, wordIndex);
        break;
      case 'split':
        // TODO: Implement word splitting
        console.log('Split word:', word.word);
        break;
      case 'cut':
        // TODO: Implement word cutting
        console.log('Cut word:', word.word);
        break;
    }
    
    // Reset all states when action is taken
    setContextMenu({ visible: false, x: 0, y: 0, word: null, wordIndex: null });
    setRightClickCount(0);
    if (rightClickTimer) {
      clearTimeout(rightClickTimer);
      setRightClickTimer(null);
    }
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, word: null, wordIndex: null });
    // Reset right click state when closing menu
    setRightClickCount(0);
    if (rightClickTimer) {
      clearTimeout(rightClickTimer);
      setRightClickTimer(null);
    }
  };

  const handleSaveSegment = (id, speaker, text) => {
    const newSegment = { ...currentSegment, speaker, text };
    const newSegments = data.transcript.segments.map((segment) => {
      if (segment.id === id) {
        return newSegment;
      }
      return segment;
    });

    const newRawText = newSegments.map((segment) => segment.text).join("");

    setTimeout(() => {
      dispatch(
        updateDataset({
          transcript: { text: newRawText, segments: newSegments },
        })
      );
    }, 0);
  };

  const handleSaveMultiple = (id, newSpeaker, text) => {
    const startIndex = data.transcript.segments.findIndex(
      (s) => s.id === selectedRange.start.id
    );
    const endIndex = selectedRange.end
      ? data.transcript.segments.findIndex((s) => s.id === selectedRange.end.id)
      : startIndex;

    const newSegments = data.transcript.segments.map((segment, index) => {
      if (index >= startIndex && index <= endIndex) {
        return { ...segment, speaker: newSpeaker };
      }
      return segment;
    });
    dispatch(updateDataset({ transcript: { segments: newSegments } }));
    setSelectedRange({ start: null, end: null });
  };

  // Speaker editing functions
  const handleSpeakerEdit = (speakerName) => {
    setEditingSpeaker(speakerName);
    setEditingSpeakerText(speakerName);
  };

  const handleSpeakerTextChange = (newText) => {
    setEditingSpeakerText(newText);
  };

  const handleSpeakerSave = () => {
    if (!editingSpeaker || !editingSpeakerText.trim()) return;

    const oldSpeaker = editingSpeaker;
    const newSpeaker = editingSpeakerText.trim();

    // Update all segments with this speaker name
    const newSegments = data.transcript.segments.map(segment => {
      if (segment.speaker === oldSpeaker) {
        return { ...segment, speaker: newSpeaker };
      }
      return segment;
    });

    // Update words array if it exists
    const newWords = data.transcript.words?.map(word => {
      if (word.speaker === oldSpeaker) {
        return { ...word, speaker: newSpeaker };
      }
      return word;
    });

    // Update speakers list
    const newSpeakers = data.speakers.map(speaker => 
      speaker === oldSpeaker ? newSpeaker : speaker
    );

    dispatch(updateDataset({
      transcript: { 
        segments: newSegments,
        words: newWords || data.transcript.words
      },
      speakers: newSpeakers
    }));

    setEditingSpeaker(null);
    setEditingSpeakerText("");
  };

  const handleSpeakerCancel = () => {
    setEditingSpeaker(null);
    setEditingSpeakerText("");
  };

  const handleSpeakerKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSpeakerSave();
    } else if (e.key === 'Escape') {
      handleSpeakerCancel();
    }
  };

  const handleScrollToTranscript = () => {
    const element = document.getElementById(`segment-${currentPlayingSegment}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const handleDelete = () => {
    swal({
      title: "Are you sure?",
      text: "You're about to delete selected segment(s). Are you sure you want to do that?",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        let idsToDelete = [];

        if (multipleSelected) {
          const startIndex = data.transcript.segments.findIndex(
            (s) => s.id === selectedRange.start.id
          );
          const endIndex = data.transcript.segments.findIndex(
            (s) => s.id === selectedRange.end.id
          );
          for (let i = startIndex; i <= endIndex; i++) {
            idsToDelete.push(data.transcript.segments[i].id);
          }
        } else {
          idsToDelete.push(currentSegment.id);
        }
        const newSegments = data.transcript.segments.filter(
          (segment) => !idsToDelete.includes(segment.id)
        );
        dispatch(updateDataset({ transcript: { segments: newSegments } }));
        setSelectedRange({ start: null, end: null });
        setCurrentSegment(null);
      }
    });
  };

  const groupedSegments = groupByConsecutiveSpeakers(data.transcript.segments);
  const multipleSelected = selectedRange.start && selectedRange.end;

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 0;
      // Only update state if value actually changes (prevents unnecessary re-renders)
      setIsInAside(prevState => {
        if (scrolled && !prevState) return true;
        if (!scrolled && prevState) return false;
        return prevState; // No change, don't trigger re-render
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // Empty deps - only set up once!

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.transcriptWrapper}>
      {data && data.transcript_status !== "COMPLETED" && (
        <div className={styles.searchReplacePanel} ref={searchReplaceRef}>
          <button 
            className={styles.searchReplaceToggle}
            onClick={() => setShowSearchReplace(!showSearchReplace)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 460 512" fill="currentColor"><path d="M220.6 130.3l-67.2 28.2V43.2L98.7 233.5l54.7-24.2v130.3l67.2-209.3zm-83.2-96.7l-1.3 4.7-15.2 52.9C80.6 106.7 52 145.8 52 191.5c0 52.3 34.3 95.9 83.4 105.5v53.6C57.5 340.1 0 272.4 0 191.6c0-80.5 59.8-147.2 137.4-158zm311.4 447.2c-11.2 11.2-23.1 12.3-28.6 10.5-5.4-1.8-27.1-19.9-60.4-44.4-33.3-24.6-33.6-35.7-43-56.7-9.4-20.9-30.4-42.6-57.5-52.4l-9.7-14.7c-24.7 16.9-53 26.9-81.3 28.7l2.1-6.6 15.9-49.5c46.5-11.9 80.9-54 80.9-104.2 0-54.5-38.4-102.1-96-107.1V32.3C254.4 37.4 320 106.8 320 191.6c0 33.6-11.2 64.7-29 90.4l14.6 9.6c9.8 27.1 31.5 48 52.4 57.4s32.2 9.7 56.8 43c24.6 33.2 42.7 54.9 44.5 60.3s.7 17.3-10.5 28.5zm-9.9-17.9c0-4.4-3.6-8-8-8s-8 3.6-8 8 3.6 8 8 8 8-3.6 8-8z"/></svg>
          </button>
          
          {showSearchReplace && (
            <div className={styles.searchReplaceControls}>
              <input
                type="text"
                placeholder="Search for..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <input
                type="text"
                placeholder="Replace with..."
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
              />
              <button 
                onClick={handleSearchReplace}
                disabled={!searchText}
              >
                Replace All
              </button>
            </div>
          )}
        </div>
        )}
        <div className={styles.transcript}>
          {/* Word-level display grouped by speakers */}
          {data.transcript.words && data.transcript.words.length > 0 ? (
            <div className={styles.wordLevelDisplay}>
              {wordsBySpeakerToRender.map((group, groupIndex) => (
                  <div key={`speaker-group-${groupIndex}`} className={styles.speakerGroup}>
                    <div className={styles.messageNumber}>{groupIndex + 1}</div>
                    <div className={styles.messageBubble}>
                      <div className={styles.speakerName}>
                        {editingSpeaker === group.speaker ? (
                          <input
                            type="text"
                            value={editingSpeakerText}
                            onChange={(e) => handleSpeakerTextChange(e.target.value)}
                            onKeyDown={handleSpeakerKeyPress}
                            autoFocus
                            style={{
                              border: '2px solid #007bff',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: 'inherit',
                              fontWeight: 'inherit',
                              background: 'white',
                              minWidth: '100px'
                            }}
                          />
                        ) : (
                          <span
                            onClick={() => handleSpeakerEdit(group.speaker)}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleSpeakerEdit(group.speaker);
                            }}
                            style={{
                              cursor: 'pointer',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              border: '1px solid transparent',
                              transition: 'all 0.2s ease',
                              pointerEvents: 'auto',
                              zIndex: 10,
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f0f0f0';
                              e.target.style.borderColor = '#ddd';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.borderColor = 'transparent';
                            }}
                            title="Click to edit speaker name"
                          >
                            {group.speaker}:
                          </span>
                        )}
                      </div>
                      <div className={styles.wordsContainer}>
                        {group.words.map((word) => {
                          const isCurrentWord = currentPlayingWord !== null && currentPlayingWord === word.index;
                        return (
                          <span
                            key={`word-${word.index}`}
                            className={`${styles.word} ${
                              isCurrentWord ? styles.wordPlaying : ""
                            } ${editingWord === word.index ? styles.wordEditing : ""}`}
                            onClick={(e) => {
                              // Only handle left click, not right click
                              if (e.button === 0) {
                                e.stopPropagation();
                                handleWordClick(word.start);
                              }
                            }}
                            onMouseDown={(e) => {
                              // Handle right click for double click detection (Mac compatible)
                              if (e.button === 2 || e.which === 3 || (e.buttons === 2)) {
                                console.log('🖱️ Right click detected, button:', e.button, 'which:', e.which, 'buttons:', e.buttons);
                                e.preventDefault();
                                e.stopPropagation();
                                
                                setRightClickCount(prev => {
                                  const newCount = prev + 1;
                                  if (newCount === 1) {
                                    // First right click - start timer
                                    const timer = setTimeout(() => {
                                      setRightClickCount(0);
                                    }, 300); // 300ms window for double click
                                    setRightClickTimer(timer);
                                  } else if (newCount === 2) {
                                    // Second right click within 300ms - open menu
                                    if (rightClickTimer) {
                                      clearTimeout(rightClickTimer);
                                      setRightClickTimer(null);
                                    }
                                    console.log('🖱️ Right double-click detected via mouseDown');
                                    handleContextMenu(e, word, word.index);
                                    setRightClickCount(0);
                                  }
                                  return newCount;
                                });
                                return;
                              }
                              
                              if (e.button === 0) { // Only left mouse button
                                e.preventDefault();
                                e.stopPropagation();
                                // Start long click timer
                                const longClickTimer = setTimeout(() => {
                                  handleWordEdit(word, word.index);
                                }, 500); // 500ms for long click
                                
                                const handleMouseUp = () => {
                                  clearTimeout(longClickTimer);
                                  document.removeEventListener('mouseup', handleMouseUp);
                                };
                                
                                document.addEventListener('mouseup', handleMouseUp);
                              }
                            }}
                            onDoubleClick={(e) => {
                              console.log('🖱️ Double click detected, button:', e.button, 'buttons:', e.buttons, 'which:', e.which);
                              e.preventDefault();
                              e.stopPropagation();
                              e.nativeEvent.stopImmediatePropagation();
                              
                              // Check if it's right double click (Mac compatibility)
                              if (e.button === 2 || e.which === 3 || (e.buttons === 2)) {
                                console.log('🖱️ Right double-click context menu triggered');
                                handleContextMenu(e, word, word.index);
                              } else {
                                // Left double click - jump to timestamp
                                console.log('🖱️ Left double-click - jumping to timestamp');
                                handleWordClick(word.start);
                              }
                            }}
                            onContextMenu={(e) => {
                              console.log('🖱️ Context menu event triggered');
                              // Prevent default context menu
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            title={`Click to jump to ${word.start.toFixed(2)}s - ${word.end.toFixed(2)}s${word.probability ? ` (${(word.probability * 100).toFixed(1)}% confidence)` : ''} | Long press to edit | Right double-click for menu`}
                          >
                            {editingWord === word.index ? (
                              <input
                                type="text"
                                value={editingWordText}
                                onChange={(e) => setEditingWordText(e.target.value)}
                                onBlur={handleWordSave}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleWordSave();
                                  } else if (e.key === 'Escape') {
                                    handleWordCancel();
                                  }
                                }}
                                className={styles.wordInput}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              word.word
                            )}
                          </span>
                        );
                        })}
                  </div>
                </div>
                  </div>
                ))}
                </div>
          ) : (
            // Fallback to segments if no word data
            <div style={{padding: '20px', background: '#ffebee', border: '2px solid #f44336', borderRadius: '8px', margin: '20px 0'}}>
              <h3 style={{color: '#d32f2f', margin: '0 0 10px 0'}}>⚠️ Fallback to segments - No word data available</h3>
              <p style={{color: '#666', margin: '0'}}>Words length: {data.transcript.words?.length || 'undefined'}</p>
            </div>
          )}
          {showPopup && currentSegment != null && (
            <SegmentPopup
              segment={currentSegment}
              onClose={handleClosePopup}
              onSave={multipleSelected ? handleSaveMultiple : handleSaveSegment}
              multipleSelected={multipleSelected}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>
      
      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className={styles.contextMenu}
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.contextMenuContent}>
            <button
              className={styles.contextMenuItem}
              onClick={() => handleContextMenuAction('play')}
              title="Play from this word"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5,3 19,12 5,21"></polygon>
              </svg>
            </button>
            <button
              className={styles.contextMenuItem}
              onClick={() => handleContextMenuAction('edit')}
              title="Edit word"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <button
              className={styles.contextMenuItem}
              onClick={() => handleContextMenuAction('split')}
              title="Split word"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
              </svg>
            </button>
            <button
              className={styles.contextMenuItem}
              onClick={() => handleContextMenuAction('cut')}
              title="Cut word"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="9" r="2"></circle>
                <circle cx="15" cy="15" r="2"></circle>
                <path d="M8.5 8.5L15.5 15.5"></path>
                <path d="M15.5 8.5L8.5 15.5"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <aside className={styles.sidebar} ref={stickyAsideRef} style={{ maxHeight: window.innerWidth > 991 && `calc(100vh - ${headerHeight + 40}px` }}>
          <div className={styles.mediaWrapper}>
            {isVideo ? (
              <div className={styles.mediaContainer}>
                <video
                  ref={mediaRef}
                  className={styles.media}
                  id="mediaTranscript"
                  controls
                  src={`/media/${data.file_id}.${data.file_type}`}
                />
                <div
                  className={styles.button}
                  onClick={handleScrollToTranscript}
                >
                  Click to scroll to this part of the transcript
                </div>
              </div>
            ) : (
              <div className={styles.mediaContainer}>
                <audio
                  ref={mediaRef}
                  className={styles.media}
                  id="mediaTranscript"
                  controls
                  src={`/media/${data.file_id}.${data.file_type}`}
                />
                <div
                  className={styles.button}
                  onClick={handleScrollToTranscript}
                >
                  Click to scroll to this part of the transcript
                </div>
              </div>
            )}
          </div>
        {data && data.moderator && data.moderator !== "" && (
          <TranscriptModerator />
        )}
        <SpeakerList />
        {data && data.transcript_status === "COMPLETED" ? (
          <>
          <ExtraButtons report={data.report_url} advisor={data.advisor} />
          <ExportTranscript />
          </>
        ) : null}
        <ScrollToTop />
      </aside>
      
      {/* Debug Panel - Remove in production */}
      <DebugPanel 
        mediaRef={mediaRef}
        currentPlayingWord={currentPlayingWord}
        words={data.transcript.words || []}
        lastWordIndexRef={lastWordIndexRef}
      />
    </div>
  );
}

export default Transcript;
